import React, { useRef, useState, DragEvent, ChangeEvent } from 'react';
import { Upload, X, File, CheckCircle2, AlertCircle } from 'lucide-react';

export interface FileUploadProps {
    label?: string;
    accept?: string;
    maxSize?: number; // in bytes
    onFileSelect: (file: File) => void;
    onFileRemove?: () => void;
    error?: string;
    helperText?: string;
    disabled?: boolean;
    currentFile?: File | null;
    showPreview?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
    label,
    accept = '*/*',
    maxSize = 5 * 1024 * 1024, // 5MB default
    onFileSelect,
    onFileRemove,
    error,
    helperText,
    disabled = false,
    currentFile = null,
    showPreview = true
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadError, setUploadError] = useState<string>('');

    const validateFile = (file: File): string | null => {
        // Check file size
        if (file.size > maxSize) {
            return `File size must be less than ${(maxSize / (1024 * 1024)).toFixed(0)}MB`;
        }

        // Check file type if accept is specified
        if (accept !== '*/*') {
            const acceptedTypes = accept.split(',').map(type => type.trim());
            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
            const mimeType = file.type;

            const isAccepted = acceptedTypes.some(type => {
                if (type.startsWith('.')) {
                    return fileExtension === type.toLowerCase();
                }
                return mimeType.match(new RegExp(type.replace('*', '.*')));
            });

            if (!isAccepted) {
                return `File type not accepted. Allowed types: ${accept}`;
            }
        }

        return null;
    };

    const handleFile = (file: File) => {
        setUploadError('');
        const validationError = validateFile(file);

        if (validationError) {
            setUploadError(validationError);
            return;
        }

        onFileSelect(file);
    };

    const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (disabled) return;

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFile(files[0]);
        }
    };

    const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
    };

    const handleRemoveFile = () => {
        setUploadError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onFileRemove?.();
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const displayError = error || uploadError;

    return (
        <div className="w-full">
            {label && (
                <label className="block mb-2 text-sm font-medium text-[#015FA6] font-manrope">
                    {label}
                </label>
            )}

            <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => !disabled && !currentFile && fileInputRef.current?.click()}
                className={`
                    relative border-2 border-dashed rounded-lg
                    transition-all duration-200 cursor-pointer
                    ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:border-[#015FA6]'}
                    ${isDragging ? 'border-[#015FA6] bg-[#015FA6]/5' : displayError ? 'border-red-500 bg-red-50/50' : 'border-[#CBD5E0] bg-white'}
                    ${currentFile && showPreview ? 'p-4' : 'p-8'}
                `}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFileInputChange}
                    disabled={disabled}
                    className="hidden"
                    aria-label={label || 'File upload'}
                />

                {currentFile && showPreview ? (
                    <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-[#015FA6]/10 rounded-lg flex items-center justify-center">
                            <File className="w-5 h-5 text-[#015FA6]" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate font-manrope">
                                {currentFile.name}
                            </p>
                            <p className="text-xs text-gray-500 font-manrope">
                                {formatFileSize(currentFile.size)}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveFile();
                                    }}
                                    className="p-1 hover:bg-red-100 rounded-full transition-colors"
                                    aria-label="Remove file"
                                >
                                    <X className="w-4 h-4 text-red-500" />
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center text-center">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                            displayError ? 'bg-red-100' : 'bg-[#015FA6]/10'
                        }`}>
                            {displayError ? (
                                <AlertCircle className="w-6 h-6 text-red-500" />
                            ) : (
                                <Upload className="w-6 h-6 text-[#015FA6]" />
                            )}
                        </div>
                        <p className="text-sm font-medium text-gray-700 mb-1 font-manrope">
                            {isDragging ? 'Drop file here' : 'Click to upload or drag and drop'}
                        </p>
                        <p className="text-xs text-gray-500 font-manrope">
                            {accept === '*/*' ? 'Any file type' : accept.replace(/\./g, '').toUpperCase()}
                            {' • '}Max size: {(maxSize / (1024 * 1024)).toFixed(0)}MB
                        </p>
                    </div>
                )}
            </div>

            {displayError && (
                <p role="alert" className="mt-1.5 text-xs text-red-500 font-manrope flex items-center gap-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
                        <path
                            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                            stroke="currentColor"
                            strokeWidth={1.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    {displayError}
                </p>
            )}
            {helperText && !displayError && (
                <p className="mt-1.5 text-xs text-gray-500 font-manrope">
                    {helperText}
                </p>
            )}
        </div>
    );
};

export default FileUpload;
